import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { App, Card, Row, Col, Typography, Spin, Button, Tag, Collapse, Tooltip, Table, DatePicker, Select, Empty } from 'antd'
import { PageHeader } from '../../design-system/PageHeader'
import type { ColumnsType } from 'antd/es/table'
import { ReloadOutlined, ExportOutlined, ExclamationCircleFilled, RightOutlined, SettingOutlined } from '@ant-design/icons'
import { useManualQuery } from 'graphql-hooks'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'
import { getAuthTokens } from '@vacationtracker/shared/services/auth'
import axios from 'axios'
import { IADPUsersResponse } from '@vacationtracker/shared/types/adp'
import { useAppSelector } from '../../store/hooks'
import { selectAuthCompanySlice } from '../../store/auth-company-slice'
import logger from '@vacationtracker/shared/functions/logger'
import { v4 as uuidv4 } from 'uuid'
import { useIntl } from 'react-intl'
import dayjs, { Dayjs } from 'dayjs'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ADPMarketplaceBtn, ADP_MARKETPLACE_URL } from './marketplace-btn'
import { ThemeContext } from '../../context/themeContext'
import { DARK_THEME } from '@vacationtracker/shared/constants/ThemeSetting'
import YoutubeEmbed from '../youtube-embed'
import { PiCheckCircle, PiArrowsClockwise, PiChartBar } from 'react-icons/pi'

import { AddonsStatus, AddonsStatusEnum } from '@vacationtracker/shared/types/addons'
import { IAddonsData } from '@vacationtracker/shared/types/automations'
import { SubscriptionPlan } from '@vacationtracker/shared/types/company'
import { useCurrency } from '../../hooks/use-currency'

interface IAdpAddonViewProps {
  status?: AddonsStatus
  handleSubscribe?: (automationType: string, isActive: boolean) => void
  addonDetails?: IAddonsData
  currentPlan?: SubscriptionPlan
  authUserId?: string
}

interface ILeavePolicyShort {
  isActive: boolean
  shortestLeaveInterval?: string
  allowHalfDays?: boolean
  quotaSetIn?: string
}

interface ILeaveTypeForAdp {
  id: string
  name: string
  isActive: boolean
  position: number
  leavePolicies: ILeavePolicyShort[]
}

interface IGetLeaveTypesForAdp {
  getLeaveTypeList: ILeaveTypeForAdp[]
}

const getLeaveTypesForAdp = /* GraphQL */ `
  query GetLeaveTypesForAdp {
    getLeaveTypeList {
      id
      name
      isActive
      position
      leavePolicies {
        isActive
        shortestLeaveInterval
        allowHalfDays
        quotaSetIn
      }
    }
  }
`

const getCompanyActiveUsers = /* GraphQL */ `
  query GetCompanyActiveUsers {
    getCompany {
      numberOfActiveUsers
    }
  }
`

interface IGetCompanyActiveUsers {
  getCompany: {
    numberOfActiveUsers: number
  }
}

type IntegrationEventStatus = 'pending' | 'processing' | 'succeeded' | 'failed'

interface IIntegrationEvent {
  companyId: string
  integrationType: string
  id: string
  created: string
  modified: string
  type: string
  status: IntegrationEventStatus
  attempt: number
  payload?: any
  response?: any
}

interface IPaginatedIntegrationEvents {
  items: IIntegrationEvent[]
  lastEvaluatedKey?: string
}

interface IAuditFilters {
  from?: string
  to?: string
  status?: IntegrationEventStatus
}

const STATUS_TAG_CONFIG: Record<IntegrationEventStatus, { color: string; darkColor: string; background: string; darkBackground: string; i18nKey: string }> = {
  succeeded: { color: '#15803d', darkColor: '#4ade80', background: '#f0fdf4', darkBackground: '#14532d33', i18nKey: 'addons.adp.audit.statusSucceeded' },
  failed: { color: '#b91c1c', darkColor: '#f87171', background: '#fef2f2', darkBackground: '#7f1d1d33', i18nKey: 'addons.adp.audit.statusFailed' },
  pending: { color: '#92400e', darkColor: '#fbbf24', background: '#fffbeb', darkBackground: '#78350f33', i18nKey: 'app.pending' },
  processing: { color: '#1d4ed8', darkColor: '#60a5fa', background: '#eff6ff', darkBackground: '#1e3a5f33', i18nKey: 'addons.adp.audit.statusProcessing' },
}

const AUDIT_PAGE_SIZE = 10

const ADP_PLANS = [
  { tier: 'addons.adp.planTierSmall', range: '1–50', yearly: 120, monthly: 10, max: 50 },
  { tier: 'addons.adp.planTierMedium', range: '51–500', yearly: 480, monthly: 40, max: 500 },
  { tier: 'addons.adp.planTierLarge', range: '501–1,500', yearly: 960, monthly: 80, max: 1500 },
]

const { Title, Paragraph, Text } = Typography

const AdpAddonView = ({ status, handleSubscribe, addonDetails, currentPlan, authUserId }: IAdpAddonViewProps) => {
  const { notification } = App.useApp()
  const { formatMessage } = useIntl()
  const { formatPrice } = useCurrency()
  const { theme } = useContext(ThemeContext)
  const isDark = theme === DARK_THEME
  const [data, setData] = useState<IADPUsersResponse | null>(null)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const [loading, setLoading] = useState(true)
  const [leaveTypes, setLeaveTypes] = useState<ILeaveTypeForAdp[]>([])
  const [activeUserCount, setActiveUserCount] = useState<number>(0)
  const [refreshing, setRefreshing] = useState(false)
  const [auditEvents, setAuditEvents] = useState<IIntegrationEvent[]>([])
  const [auditLoading, setAuditLoading] = useState(false)
  const [auditTotalCount, setAuditTotalCount] = useState<number>(0)
  const [auditFilters, setAuditFilters] = useState<IAuditFilters>({})

  const isConnected = status === AddonsStatusEnum.ACTIVE || status === AddonsStatusEnum.GRANT_PERIOD

  const hasActiveFilters = !!(auditFilters.from || auditFilters.to || auditFilters.status)

  const [getLeaveTypes] = useManualQuery<IGetLeaveTypesForAdp>(getLeaveTypesForAdp)
  const [getActiveUsers] = useManualQuery<IGetCompanyActiveUsers>(getCompanyActiveUsers)

  useEffect(() => {
    if (isConnected) {
      getAdpUsersInfo()
      fetchAuditEvents()
    } else {
      setLoading(false)
      fetchSetupData()
    }
  }, [isConnected])

  const fetchSetupData = async () => {
    try {
      const [leaveTypeResponse, activeUsersResponse] = await Promise.all([getLeaveTypes(), getActiveUsers()])
      if (leaveTypeResponse.data?.getLeaveTypeList) {
        setLeaveTypes(leaveTypeResponse.data.getLeaveTypeList)
      }
      if (activeUsersResponse.data?.getCompany?.numberOfActiveUsers) {
        setActiveUserCount(activeUsersResponse.data.getCompany.numberOfActiveUsers)
      }
    } catch (error) {
      logger.error('Failed to fetch setup data', error)
    }
  }

  const fetchAuditEvents = useCallback(
    async (filters?: IAuditFilters) => {
      setAuditLoading(true)
      try {
        const tokens = await getAuthTokens()
        const activeFilters = filters ?? auditFilters
        const params: Record<string, string> = { limit: '100' }
        if (activeFilters.from) params.from = activeFilters.from
        if (activeFilters.to) params.to = activeFilters.to
        if (activeFilters.status) params.status = activeFilters.status

        const response = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/adp/events`, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens?.idToken}`,
          },
          params,
        })) as { data: IPaginatedIntegrationEvents }

        setAuditEvents(response.data.items)

        // Track unfiltered total for the count indicator
        if (activeFilters.from || activeFilters.to || activeFilters.status) {
          // Only fetch total if we have filters active and don't have a total yet
          if (auditTotalCount === 0) {
            const totalResponse = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/adp/events`, {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${tokens?.idToken}`,
              },
              params: { limit: '100' },
            })) as { data: IPaginatedIntegrationEvents }
            setAuditTotalCount(totalResponse.data.items.length)
          }
        } else {
          setAuditTotalCount(response.data.items.length)
        }
      } catch (error) {
        logger.error('Failed to fetch audit events', error)
        setAuditEvents([])
      } finally {
        setAuditLoading(false)
      }
    },
    [auditFilters, auditTotalCount]
  )

  const handleAuditFilterChange = (key: keyof IAuditFilters, value: string | undefined) => {
    const newFilters = { ...auditFilters, [key]: value || undefined }
    setAuditFilters(newFilters)
    fetchAuditEvents(newFilters)
  }

  const handleClearFilters = () => {
    setAuditFilters({})
    fetchAuditEvents({})
  }

  const getAdpUsersInfo = async () => {
    try {
      const tokens = await getAuthTokens()
      const response = (await axios.get(`${process.env.REACT_APP_API_URL}/integrations/adp/users-stats`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.idToken}`,
        },
        params: {
          platformToken: localStorage.getItem('vt-onboarding-platform-token'),
        },
      })) as { data: IADPUsersResponse }
      setData(response.data)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setData(null)
      const id = uuidv4()
      notification.error({
        message: formatMessage({ id: 'notifications.error' }, { correlationId: id }),
        description: formatMessage({ id: 'onboarding.adp.usersStep.failed' }),
        duration: 100,
      })
      logger.error(id, error)
    }
  }

  const handleRefreshStatus = () => {
    setRefreshing(true)
    window.location.reload()
  }

  const getIntervalLabel = (interval?: string): string => {
    switch (interval) {
      case 'ONE-HOUR':
        return formatMessage({ id: 'app.one-hour' })
      case 'HALF-DAY':
        return formatMessage({ id: 'app.half-day' })
      case 'FULL-DAY':
        return formatMessage({ id: 'app.full-day' })
      case 'HALF-HOUR':
        return formatMessage({ id: 'app.half-hour' })
      case 'FIFTEEN-MINUTES':
        return formatMessage({ id: 'app.fifteen-minutes' })
      default:
        return formatMessage({ id: 'addons.adp.step1.interval.notSet' })
    }
  }

  const getLeaveTypeDetails = (lt: ILeaveTypeForAdp) => {
    const activePolicies = lt.leavePolicies.filter((p) => p.isActive)
    if (activePolicies.length === 0) return null

    const INTERVAL_RANK: Record<string, number> = {
      'FIFTEEN-MINUTES': 0,
      'HALF-HOUR': 1,
      'ONE-HOUR': 2,
      'HALF-DAY': 3,
      'FULL-DAY': 4,
    }

    // Get the shortest (most granular) interval across all active policies
    const intervals = activePolicies.map((p) => p.shortestLeaveInterval).filter(Boolean) as string[]
    const interval = intervals.length > 0 ? intervals.reduce((shortest, cur) => ((INTERVAL_RANK[cur] ?? 99) < (INTERVAL_RANK[shortest] ?? 99) ? cur : shortest)) : undefined
    const isAdpCompliant = interval !== undefined && (INTERVAL_RANK[interval] ?? 99) <= INTERVAL_RANK['ONE-HOUR']

    return { interval, isAdpCompliant }
  }

  const getRecommendedPlanIndex = () => {
    if (activeUserCount <= 50) return 0
    if (activeUserCount <= 500) return 1
    return 2
  }

  const renderNotConnectedView = () => {
    const recommendedIdx = getRecommendedPlanIndex()

    return (
      <div style={{ maxWidth: 960, margin: '0 auto' }}>
        {/* Hero Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={24} align="top">
            <Col xs={24} md={14}>
              <Title level={4} style={{ marginBottom: 8 }}>
                <IntlMessages id="addons.adp.hero.title" />
              </Title>
              <Paragraph style={{ color: isDark ? '#AFBACA' : '#555', lineHeight: 1.65, marginBottom: 16 }}>
                <IntlMessages id="addons.adp.hero.description" />
              </Paragraph>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiCheckCircle size={14} /> <IntlMessages id="addons.adp.hero.pill1" />
                </Tag>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiArrowsClockwise size={14} /> <IntlMessages id="addons.adp.hero.pill2" />
                </Tag>
                <Tag color="purple" style={{ borderRadius: 20, padding: '4px 12px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  <PiChartBar size={14} /> <IntlMessages id="addons.adp.hero.pill3" />
                </Tag>
              </div>
            </Col>
            <Col xs={24} md={10}>
              <div style={{ marginTop: 8 }}>
                <YoutubeEmbed embedId="my1F-5fPS1c" title="Meet Carmen - Your Account Manager" />
              </div>
            </Col>
          </Row>
        </div>

        {/* Setup Guide Heading */}
        <Title level={4} style={{ marginTop: 28, marginBottom: 4 }}>
          <IntlMessages id="addons.adp.setup.heading" />
        </Title>
        <Paragraph style={{ color: isDark ? '#6B7280' : '#8c8c8c', marginBottom: 20 }}>
          <IntlMessages id="addons.adp.setup.subheading" />
        </Paragraph>

        {/* Step 1: Verify leave type configuration */}
        <Card style={{ marginBottom: 12, borderColor: isDark ? '#665500' : '#fde68a', background: isDark ? '#33280033' : '#fffdf5' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#f59e0b',
              }}
            >
              1
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step1.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.adp.step1.description" />
              </Text>
              <div style={{ marginTop: 12 }}>
                <Collapse
                  ghost
                  expandIcon={({ isActive }) => <RightOutlined rotate={isActive ? 90 : 0} style={{ color: '#b45309' }} />}
                  items={[
                    {
                      key: '1',
                      label: (
                        <Text strong style={{ color: '#b45309', fontSize: 13 }}>
                          {formatMessage({ id: 'addons.adp.step1.reviewLeaveTypes' }, { count: leaveTypes.filter((lt) => lt.isActive).length })}
                        </Text>
                      ),
                      children: (
                        <div style={{ background: isDark ? '#252B35' : '#fff', border: `1px solid ${isDark ? '#665500' : '#fde68a'}`, borderRadius: 8, padding: 16 }}>
                          <Text strong style={{ color: '#92400e', fontSize: 13, display: 'block', marginBottom: 12 }}>
                            <IntlMessages id="addons.adp.step1.checklistTitle" />
                          </Text>
                          {leaveTypes
                            .filter((lt) => lt.isActive)
                            .map((lt) => {
                              const details = getLeaveTypeDetails(lt)
                              const isAdpCompliant = details?.isAdpCompliant ?? false
                              return (
                                <div
                                  key={lt.id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: 10,
                                    padding: '10px 0',
                                    borderBottom: `1px solid ${isDark ? '#3d3000' : '#fef3c7'}`,
                                    fontSize: 13,
                                  }}
                                >
                                  <ExclamationCircleFilled style={{ color: isAdpCompliant ? '#22c55e' : '#f59e0b', fontSize: 18, marginTop: 1 }} />
                                  <div>
                                    <Text strong>{lt.name}</Text>
                                    <Text style={{ display: 'block', fontSize: 11, color: isDark ? '#6B7280' : '#8c8c8c', marginTop: 2 }}>
                                      {formatMessage({ id: 'addons.adp.step1.verifyDetails' }, { interval: getIntervalLabel(details?.interval) })}
                                    </Text>
                                    {!isAdpCompliant && (
                                      <Text style={{ display: 'block', fontSize: 11, color: '#f59e0b', marginTop: 2 }}>
                                        <IntlMessages id="addons.adp.step1.intervalWarning" />
                                      </Text>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${isDark ? '#665500' : '#fde68a'}` }}>
                            <Link to={FrontendUrls.leaveTypes || '/app/leave-types'}>
                              <Button type="default" size="small" icon={<SettingOutlined />}>
                                <IntlMessages id="addons.adp.step1.goToSettings" />
                              </Button>
                            </Link>
                          </div>
                        </div>
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Step 2: Go to ADP Marketplace */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              2
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step2.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.adp.step2.description" />
              </Text>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Button type="primary" icon={<ExportOutlined />} href={ADP_MARKETPLACE_URL} target="_blank" rel="noopener noreferrer">
                  <IntlMessages id="addons.adp.step2.buttonUS" />
                </Button>
                <Tooltip title={formatMessage({ id: 'addons.adp.step2.comingSoon' })}>
                  <Button disabled icon={<ExportOutlined />}>
                    <IntlMessages id="addons.adp.step2.buttonCanada" />
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 3: Choose a plan */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              3
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step3.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                {formatMessage(
                  { id: 'addons.adp.step3.description' },
                  {
                    count: activeUserCount,
                    plan: formatMessage({ id: ADP_PLANS[recommendedIdx].tier }),
                  }
                )}
              </Text>
              <div style={{ marginTop: 14 }}>
                <Row gutter={10}>
                  {ADP_PLANS.map((plan, i) => {
                    const isRecommended = i === recommendedIdx
                    return (
                      <Col xs={24} sm={8} key={plan.tier}>
                        <Card
                          size="small"
                          style={{
                            textAlign: 'center',
                            marginBottom: 10,
                            borderColor: isRecommended ? '#7f00ff' : isDark ? '#495862' : '#e8e8e8',
                            background: isRecommended ? (isDark ? '#2a1a4e' : '#f0e2fe') : isDark ? '#252B35' : '#fff',
                            opacity: isRecommended ? 1 : 0.5,
                            position: 'relative',
                            boxShadow: isRecommended ? '0 0 0 1px #7f00ff, 0 2px 8px rgba(127,0,255,0.15)' : undefined,
                          }}
                        >
                          {isRecommended && (
                            <Tag
                              color="purple"
                              style={{
                                position: 'absolute',
                                top: -10,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                fontSize: 10,
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                              }}
                            >
                              <IntlMessages id="addons.adp.step3.recommended" />
                            </Tag>
                          )}
                          <Text style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#6B7280' : '#8c8c8c', display: 'block', marginBottom: 4 }}>
                            <IntlMessages id={plan.tier} />
                          </Text>
                          <Text style={{ fontSize: 20, fontWeight: 700 }}>{formatPrice(plan.yearly)}</Text>
                          <Text style={{ fontSize: 12, color: isDark ? '#6B7280' : '#8c8c8c' }}> / {formatMessage({ id: 'addons.adp.step3.year' })}</Text>
                          <div>
                            <Text style={{ fontSize: 11, color: isDark ? '#6B7280' : '#8c8c8c' }}>
                              {formatMessage({ id: 'addons.adp.step3.orMonthly' }, { price: formatPrice(plan.monthly) })}
                            </Text>
                          </div>
                          <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${isDark ? '#495862' : '#e8e8e8'}` }}>
                            <Text style={{ fontSize: 11, color: isDark ? '#AFBACA' : '#666' }}>
                              {plan.range} {formatMessage({ id: 'addons.adp.step3.users' })}
                            </Text>
                          </div>
                        </Card>
                      </Col>
                    )
                  })}
                </Row>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 4: Paste User ID */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              4
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step4.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.adp.step4.description" />
              </Text>
              <div
                style={{
                  background: isDark ? '#2D3748' : '#f0f4f8',
                  border: `1px dashed ${isDark ? '#495862' : '#b0bec5'}`,
                  borderRadius: 8,
                  padding: '8px 12px',
                  marginTop: 10,
                  display: 'inline-block',
                }}
              >
                <Paragraph copyable={{ text: authUserId }} style={{ marginBottom: 0, fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>
                  {authUserId}
                </Paragraph>
              </div>
            </div>
          </div>
        </Card>

        {/* Step 5: Complete the purchase */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              5
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step5.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.adp.step5.description" />
              </Text>
            </div>
          </div>
        </Card>

        {/* Step 6: Verify the connection */}
        <Card style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 14,
                fontWeight: 700,
                color: '#fff',
                background: '#7f00ff',
              }}
            >
              6
            </div>
            <div style={{ flex: 1 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                <IntlMessages id="addons.adp.step6.title" />
              </Text>
              <Text style={{ fontSize: 13, color: isDark ? '#AFBACA' : '#666' }}>
                <IntlMessages id="addons.adp.step6.description" />
              </Text>
              <div style={{ marginTop: 12 }}>
                <Button type="primary" icon={<ReloadOutlined />} loading={refreshing} onClick={handleRefreshStatus}>
                  <IntlMessages id="addons.adp.step6.button" />
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  const auditColumns: ColumnsType<IIntegrationEvent> = [
    {
      title: formatMessage({ id: 'addons.adp.audit.column.dateTime' }),
      dataIndex: 'created',
      key: 'created',
      width: 170,
      render: (created: string) => dayjs(created).format('MMM D, YYYY HH:mm'),
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.eventType' }),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag style={{ fontFamily: 'monospace', fontSize: 11 }}>{type}</Tag>,
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.entity' }),
      dataIndex: 'id',
      key: 'id',
      render: (id: string) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{id}</Text>,
    },
    {
      title: formatMessage({ id: 'app.status' }),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (eventStatus: IntegrationEventStatus) => {
        const config = STATUS_TAG_CONFIG[eventStatus]
        return (
          <Tag
            style={{
              color: isDark ? config.darkColor : config.color,
              background: isDark ? config.darkBackground : config.background,
              border: 'none',
              fontWeight: 600,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.3,
            }}
          >
            {formatMessage({ id: config.i18nKey })}
          </Tag>
        )
      },
    },
    {
      title: formatMessage({ id: 'addons.adp.audit.column.attempt' }),
      dataIndex: 'attempt',
      key: 'attempt',
      width: 80,
      render: (attempt: number) => formatMessage({ id: 'addons.adp.audit.attempt' }, { current: attempt }),
    },
  ]

  const renderConnectedView = () => {
    return (
      <>
        {loading && (
          <div className="users-step-loading-container">
            <Spin size="large" className="users-step-spinner" />
            <IntlMessages id="onboarding.adp.usersStep.loading" />
          </div>
        )}
        {!loading && data && (
          <div className="adp-addon-view" style={{ maxWidth: 960, margin: '0 auto' }}>
            <Card className="users-step-card" variant="outlined">
              <Row gutter={{ xs: 16, sm: 24, md: 32, lg: 48, xl: 72 }} justify="space-between" align="middle">
                {data.seats > 0 && (
                  <Col xs={24} sm={12} md={8} lg={8} xl={8} className="users-step-col">
                    <Title level={2} className="users-step-number">
                      {data.seats}
                    </Title>
                    <Text className="users-step-label">
                      <IntlMessages id="addons.adp.seats" />
                    </Text>
                  </Col>
                )}
                <Col xs={24} sm={12} md={8} lg={8} xl={8} className="users-step-col">
                  <Title level={2} className="users-step-number">
                    {data?.adp?.total}
                    {data?.adp?.total === 1500 ? '+' : ''}
                  </Title>
                  <Text className="users-step-label">
                    <IntlMessages id="onboarding.adp.usersStep.info5" />
                  </Text>
                </Col>
                <Col xs={24} sm={12} md={8} lg={8} xl={8} className="users-step-col">
                  <Title level={2} className="users-step-number">
                    {data?.adp?.integrated}
                  </Title>
                  <Text className="users-step-label">
                    <IntlMessages id="app.integrated" />
                  </Text>
                </Col>
              </Row>
            </Card>
            <Paragraph className="users-step-info" style={{ marginTop: 16 }}>
              <IntlMessages id="onboarding.adp.emailMatchInfo" />
            </Paragraph>
            <Row gutter={{ xs: 12, sm: 16, md: 24 }} justify="start" align="middle" style={{ marginTop: 8 }}>
              {data.seats > 0 && (
                <Col>
                  <Text>
                    <IntlMessages id="addons.adp.needMoreSeats" />{' '}
                  </Text>
                  <ADPMarketplaceBtn />
                </Col>
              )}
              <Col>
                <Text>
                  <IntlMessages id="addons.adp.needMoreUsers" />{' '}
                </Text>
                <Link to={authCompany?.platform === PlatformEnum.Email ? '/app/users/import-email-users' : '/app/users/import'}>
                  <Button type="link" size="large">
                    <IntlMessages id={'users.importUsers'} />
                  </Button>
                </Link>
              </Col>
              <Col>
                <Text>
                  <IntlMessages id="addons.adp.visitMarketplace" />{' '}
                </Text>
                <ADPMarketplaceBtn />
              </Col>
            </Row>

            {/* Audit Log Section */}
            <div style={{ marginTop: 32 }}>
              <Title level={4} style={{ marginBottom: 16 }}>
                <IntlMessages id="addons.adp.audit.title" />
              </Title>

              <Card>
                {/* Filter Bar */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 14,
                    paddingBottom: 14,
                    borderBottom: `1px solid ${isDark ? '#495862' : '#f0f0f0'}`,
                  }}
                >
                  <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c' }}>
                    <IntlMessages id="app.from" />
                  </Text>
                  <DatePicker
                    size="small"
                    value={auditFilters.from ? dayjs(auditFilters.from) : null}
                    onChange={(date: Dayjs | null) => handleAuditFilterChange('from', date?.toISOString())}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                    style={{ width: 140 }}
                  />
                  <Text style={{ color: isDark ? '#495862' : '#ccc', fontSize: 12 }}>-</Text>
                  <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c' }}>
                    <IntlMessages id="app.to" />
                  </Text>
                  <DatePicker
                    size="small"
                    value={auditFilters.to ? dayjs(auditFilters.to) : null}
                    onChange={(date: Dayjs | null) => handleAuditFilterChange('to', date?.toISOString())}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                    style={{ width: 140 }}
                  />
                  <Text style={{ fontSize: 12, fontWeight: 500, color: isDark ? '#6B7280' : '#8c8c8c', marginLeft: 8 }}>
                    <IntlMessages id="app.status" />
                  </Text>
                  <Select
                    size="small"
                    value={auditFilters.status || ''}
                    onChange={(value: string) => handleAuditFilterChange('status', value || undefined)}
                    style={{ width: 140 }}
                    options={[
                      { value: '', label: formatMessage({ id: 'addons.adp.audit.statusAll' }) },
                      { value: 'succeeded', label: formatMessage({ id: 'addons.adp.audit.statusSucceeded' }) },
                      { value: 'failed', label: formatMessage({ id: 'addons.adp.audit.statusFailed' }) },
                      { value: 'processing', label: formatMessage({ id: 'addons.adp.audit.statusProcessing' }) },
                      { value: 'pending', label: formatMessage({ id: 'app.pending' }) },
                    ]}
                  />
                  {hasActiveFilters && (
                    <Button type="link" size="small" onClick={handleClearFilters} style={{ fontSize: 12 }}>
                      <IntlMessages id="addons.adp.audit.clearFilters" />
                    </Button>
                  )}
                  {hasActiveFilters && auditEvents.length !== auditTotalCount && (
                    <Text style={{ fontSize: 12, color: isDark ? '#6B7280' : '#8c8c8c', marginLeft: 'auto' }}>
                      {formatMessage({ id: 'addons.adp.audit.showingCount' }, { filtered: auditEvents.length, total: auditTotalCount })}
                    </Text>
                  )}
                </div>

                {/* Table */}
                <Table<IIntegrationEvent>
                  columns={auditColumns}
                  dataSource={auditEvents}
                  rowKey={(record) => `${record.id}-${record.created}`}
                  loading={auditLoading}
                  size="small"
                  pagination={{
                    pageSize: AUDIT_PAGE_SIZE,
                    showSizeChanger: false,
                    hideOnSinglePage: auditEvents.length <= AUDIT_PAGE_SIZE,
                  }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div
                        style={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-all',
                          color: isDark ? '#AFBACA' : '#555',
                          background: isDark ? '#1C222B' : '#fafafa',
                          padding: 12,
                          borderRadius: 4,
                        }}
                      >
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>
                          <IntlMessages id="addons.adp.audit.payload" />:
                        </Text>
                        {record.payload ? JSON.stringify(record.payload, null, 2) : '-'}
                        <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 4 }}>
                          <IntlMessages id="addons.adp.audit.response" />:
                        </Text>
                        {record.response ? JSON.stringify(record.response, null, 2) : formatMessage({ id: 'addons.adp.audit.responseNotAvailable' })}
                      </div>
                    ),
                  }}
                  locale={{
                    emptyText: (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={hasActiveFilters ? formatMessage({ id: 'addons.adp.audit.emptyFiltered' }) : formatMessage({ id: 'addons.adp.audit.emptyState' })}
                      />
                    ),
                  }}
                />
              </Card>
            </div>
          </div>
        )}
      </>
    )
  }

  return (
    <div className="main-content addons-view-page">
      <PageHeader
        title={<IntlMessages id="automations.ADP" />}
        badge={<Tag color="orange">BETA</Tag>}
        parent={[{ label: <IntlMessages id="app.integrations" />, to: FrontendUrls.integrations }]}
      />
      <div className="main-content-body">{isConnected ? renderConnectedView() : renderNotConnectedView()}</div>
    </div>
  )
}

export default AdpAddonView
